import * as React from 'react';
import { WorkspaceButton, WorkspaceDialog, WorkspaceNotice } from '@/components/ui/workspace';
import type { LibraryTree } from '@/lib/api/requirement-library';
import { findModuleByName, plural } from './library-logic';

/**
 * Module-level curation dialogs (T-0152): retire a module, and rename one.
 * T-0157 adds retiring a category, which follows rule 2 (a category with
 * active modules cannot be retired) and rule 5 (nothing is deleted).
 *
 * Both follow the integrity rules of the E11 epic. Retiring never deletes
 * (rule 5) and keeps the name and the prefix reserved (rules 1, 6), so the
 * confirm says what retiring does and what it leaves alone. A rename goes
 * through the draft and takes effect on publish, and never changes the
 * prefix or a number (rule 4), so the dialog says that too.
 *
 * Built from the shared `WorkspaceDialog`, like the new-module and publish
 * dialogs next to it.
 */

const FIELD =
  'block h-9 w-full rounded-card border border-ink-200 bg-paper-0 px-3 text-body text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600';

export interface RetireModuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moduleName: string;
  prefix: string;
  /** Requirements in the published version. */
  itemCount: number;
  /** Unpublished changes in the draft; retiring is refused while there are any. */
  draftChanges: number;
  retiring: boolean;
  /** Server refusal, if the last attempt failed. */
  error: string | null;
  onRetire: () => void;
}

export function RetireModuleDialog({
  open,
  onOpenChange,
  moduleName,
  prefix,
  itemCount,
  draftChanges,
  retiring,
  error,
  onRetire,
}: RetireModuleDialogProps) {
  const blocked = draftChanges > 0;
  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Retire ${moduleName}?`}
      description="A retired module can be restored at any time."
      actions={
        <>
          <WorkspaceButton size="md" onClick={() => onOpenChange(false)}>
            Keep the module
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="destructive"
            disabled={blocked || retiring}
            onClick={onRetire}
          >
            {retiring ? 'Retiring…' : 'Retire module'}
          </WorkspaceButton>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-body-sm text-ink-700">
        <div>
          <p className="font-semibold text-ink-900">What retiring does</p>
          <ul className="mt-1 list-disc pl-5">
            <li>Hides {moduleName} from the requirement library.</li>
            <li>
              Removes it from the list engagements add requirements from, so nothing new is loaded
              from it.
            </li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-ink-900">What it does not affect</p>
          <ul className="mt-1 list-disc pl-5">
            <li>
              Engagements that already loaded requirements from {moduleName} keep them, unchanged.
            </li>
            <li>
              Nothing is deleted: its {plural(itemCount, 'requirement')} and its version history are
              kept.
            </li>
            <li>
              The name and the prefix <span className="font-mono">{prefix}</span> stay reserved, so
              no other module can take them.
            </li>
          </ul>
        </div>
      </div>
      {blocked ? (
        <WorkspaceNotice tone="at-risk">
          {moduleName} has {plural(draftChanges, 'unpublished change')}. Publish or discard the
          draft before retiring the module.
        </WorkspaceNotice>
      ) : null}
      {error ? <WorkspaceNotice tone="critical">{error}</WorkspaceNotice> : null}
    </WorkspaceDialog>
  );
}

export interface RetireCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categoryName: string;
  /** Names of the category's active modules; retiring is refused while there are any (rule 2). */
  activeModuleNames: ReadonlyArray<string>;
  retiring: boolean;
  /** Server refusal, if the last attempt failed. */
  error: string | null;
  onRetire: () => void;
}

export function RetireCategoryDialog({
  open,
  onOpenChange,
  categoryName,
  activeModuleNames,
  retiring,
  error,
  onRetire,
}: RetireCategoryDialogProps) {
  const blocked = activeModuleNames.length > 0;
  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Retire ${categoryName}?`}
      description="A retired category can be restored at any time."
      actions={
        <>
          <WorkspaceButton size="md" onClick={() => onOpenChange(false)}>
            Keep the category
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="destructive"
            disabled={blocked || retiring}
            onClick={onRetire}
          >
            {retiring ? 'Retiring…' : 'Retire category'}
          </WorkspaceButton>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-body-sm text-ink-700">
        <div>
          <p className="font-semibold text-ink-900">What retiring does</p>
          <ul className="mt-1 list-disc pl-5">
            <li>Hides {categoryName} from the requirement library and its tree.</li>
            <li>
              Keeps it out of new loads: nothing new can be created in it or loaded from it into an
              engagement.
            </li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-ink-900">What it does not affect</p>
          <ul className="mt-1 list-disc pl-5">
            <li>Engagements that already loaded requirements keep them, unchanged.</li>
            <li>
              Nothing is deleted: its retired modules, their requirements and their history are
              kept, and the name stays reserved.
            </li>
          </ul>
        </div>
      </div>
      {blocked ? (
        <WorkspaceNotice tone="at-risk">
          {categoryName} still has {plural(activeModuleNames.length, 'active module')} (
          {activeModuleNames.join(', ')}). A category with active modules cannot be retired: retire
          or move its modules first.
        </WorkspaceNotice>
      ) : null}
      {error ? <WorkspaceNotice tone="critical">{error}</WorkspaceNotice> : null}
    </WorkspaceDialog>
  );
}

export interface RenameModuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moduleId: string;
  /** The published name. */
  moduleName: string;
  prefix: string;
  /** A rename already waiting in the draft. */
  pendingName: string | null;
  tree: LibraryTree | undefined;
  saving: boolean;
  /** Server refusal, if the last attempt failed. */
  error: string | null;
  /** The new name; the published name withdraws a pending rename. */
  onRename: (name: string) => void;
}

export function RenameModuleDialog({
  open,
  onOpenChange,
  moduleId,
  moduleName,
  prefix,
  pendingName,
  tree,
  saving,
  error,
  onRename,
}: RenameModuleDialogProps) {
  const [name, setName] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    setName(pendingName ?? moduleName);
    setTouched(false);
    // Only when the dialog opens: typing must not reset the field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = name.trim().replace(/\s+/g, ' ');
  const clash = trimmed ? findModuleByName(tree, trimmed, moduleId) : null;
  const unchanged = trimmed === (pendingName ?? moduleName);
  const canSave = trimmed.length > 0 && !clash && !unchanged && !saving;
  // The server's refusal belongs to the name it refused.
  const serverError = error && !touched ? error : null;

  const submit = () => {
    if (canSave) {
      setTouched(false);
      onRename(trimmed);
    }
  };

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Rename ${moduleName}`}
      description="The new name is part of the draft and takes effect when you publish."
      actions={
        <>
          <WorkspaceButton size="md" onClick={() => onOpenChange(false)}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton size="md" variant="primary" disabled={!canSave} onClick={submit}>
            {saving ? 'Saving…' : 'Rename in draft'}
          </WorkspaceButton>
        </>
      }
    >
      <form
        className="flex flex-col gap-3.5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div>
          <label
            htmlFor="rl-rename-module"
            className="mb-1.5 block text-meta font-medium text-ink-700"
          >
            New name <span className="font-normal text-posture-critical">*</span>
          </label>
          <input
            id="rl-rename-module"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setTouched(true);
            }}
            autoFocus
            autoComplete="off"
            maxLength={120}
            aria-invalid={clash != null || serverError != null}
            aria-describedby="rl-rename-module-help"
            className={FIELD}
          />
          <div id="rl-rename-module-help" className="mt-1.5 text-meta">
            {clash ? (
              <span role="alert" className="text-posture-critical">
                {clash.status === 'retired'
                  ? `A module named "${clash.name}" already exists and is retired. Its name stays reserved.`
                  : `A module named "${clash.name}" already exists.`}{' '}
                Choose another name.
              </span>
            ) : serverError ? (
              <span role="alert" className="text-posture-critical">
                {serverError}
              </span>
            ) : (
              <span className="text-ink-500">
                The prefix <span className="font-mono">{prefix}</span> and every requirement number
                stay as they are.
              </span>
            )}
          </div>
        </div>
        <WorkspaceNotice>
          Until you publish, everyone still sees &ldquo;{moduleName}&rdquo;. Engagements that
          already loaded requirements from this module keep the name they loaded them under.
        </WorkspaceNotice>
      </form>
    </WorkspaceDialog>
  );
}
